import React, { useCallback, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Grid, MenuItem, Typography } from "@mui/material"
import { CreateOrEditPriceListFormValues, CustomerFlat } from "../../../priceListTypes"
import { ApiQueryTable } from "../../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useFormikContext } from "formik"
import { isEmpty } from "lodash"
import { AddCustomerDialog } from "./AddCustomerDialog"
import { AddCustomerContainer } from "./AddCustomerContainer"
import { convertCustomersByUserGroup, convertFlatToCustomers } from "../../../helpers"

const useStyles = makeStyles()((_theme) => ({
    container: {
        gap: "2rem",
        flexDirection: "column",
    },
    box: {
        border: `1px solid ${_theme.palette.tailwind.slate[100]}`,
        borderRadius: "12px",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        padding: "2rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "white",
    },
    tableContainer: {
        maxHeight: "100%",
        borderRadius: ".75rem",
        padding: "1em 1em 3em 1em",
    },
    tablePaper: {
        borderRadius: ".75rem",
    },
    tableHeadCell: {
        border: "none",
        color: _theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: _theme.palette.tailwind.slate[50],
    },
    topToolbar: {
        borderRadius: ".75rem",
    },
    addButton: {
        color: _theme.palette.tailwind.sky[500],
        borderRadius: ".5rem",
        padding: ".75rem 1rem",
    },
    emptyTitle: {
        fontSize: "1.5rem",
        fontStyle: "italic",
        color: _theme.palette.tailwind.slate[500],
    },
}))

export function FormCustomerContent() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { setFieldValue, values } = useFormikContext<CreateOrEditPriceListFormValues>()
    const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false)

    const customersFlat = useMemo(() => {
        return convertCustomersByUserGroup(values.customers)
    }, [values.customers])

    const columns = useMemo<MRT_ColumnDef<CustomerFlat>[]>(
        () => [
            {
                header: l10n.getString("create-or-edit-price-list-customer-table-header-name"),
                accessorKey: "customerName",
                size: 100,
            },
            {
                header: l10n.getString("create-or-edit-price-list-customer-table-header-user-group"),
                accessorKey: "userGroupName",
                minSize: 200,
                maxSize: 1000,
            },
        ],
        [l10n],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<CustomerFlat>; closeMenu: () => void }) => [
            <MenuItem
                key="remove"
                onClick={() => {
                    const newCustomersFlat = customersFlat.filter(
                        (item) => item.userGroupId !== row.original.userGroupId,
                    )

                    const newCustomers = convertFlatToCustomers(newCustomersFlat)
                    setFieldValue("customers", newCustomers)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="create-or-edit-price-list-customer-table-button-remove">
                    <span>Remove</span>
                </LocalizedStrict>
            </MenuItem>,
            // <MenuItem
            //     key="details"
            //     onClick={() => {
            //         closeMenu()
            //     }}
            // >
            //     <LocalizedStrict id="create-or-edit-price-list-customer-table-button-details">
            //         <span>Details</span>
            //     </LocalizedStrict>
            // </MenuItem>,
        ],
        [customersFlat, setFieldValue],
    )

    return (
        <>
            <Grid container className={classes.container}>
                <Grid item xs={12}>
                    <LocalizedStrict id="create-or-edit-price-list-customer-subtitle">
                        <Typography mt="1rem">
                            If no customers are added to the access list, then all customers have access.
                        </Typography>
                    </LocalizedStrict>
                </Grid>
                <Grid item xs={12}>
                    {isEmpty(values.customers) ? (
                        <Box className={classes.box}>
                            <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                                <AddCustomerContainer
                                    className={classes.addButton}
                                    onClick={() => setShowAddCustomerDialog(true)}
                                />
                            </Box>
                            <Box pt="2.5rem">
                                <LocalizedStrict id="create-or-edit-price-list-customer-table-empty-state">
                                    <Typography className={classes.emptyTitle} textAlign="center">
                                        All customers have access
                                    </Typography>
                                </LocalizedStrict>
                            </Box>
                        </Box>
                    ) : (
                        <ApiQueryTable
                            isLoading={false}
                            isFetching={false}
                            isError={false}
                            data={customersFlat}
                            columns={columns}
                            enableClickToCopy={false}
                            enableColumnOrdering
                            enableStickyHeader
                            enableDensityToggle={false}
                            initialState={{ density: "spacious" }}
                            enableHiding={false}
                            enableRowActions
                            enableBottomToolbar={false}
                            muiTablePaperProps={{ className: classes.tablePaper }}
                            muiTableContainerProps={{ className: classes.tableContainer }}
                            muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                            muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                            muiTopToolbarProps={{ className: classes.topToolbar }}
                            positionActionsColumn="last"
                            enableColumnDragging={false}
                            renderRowActionMenuItems={renderRowActionMenuItems}
                            renderTopToolbar={() => (
                                <Box display="flex" alignItems="center" justifyContent="space-between" padding="1rem">
                                    <AddCustomerContainer
                                        className={classes.addButton}
                                        onClick={() => setShowAddCustomerDialog(true)}
                                    />
                                </Box>
                            )}
                        />
                    )}
                </Grid>
                {showAddCustomerDialog && (
                    <AddCustomerDialog open={showAddCustomerDialog} onClose={() => setShowAddCustomerDialog(false)} />
                )}
            </Grid>
        </>
    )
}
